import type { SyntheticEvent } from 'react';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import type { Channel } from '@t/productOffering.types';
import { ChannelHoverCard } from '@components/ProductOffers/misc/ChannelHoverCard';
import { channelTooltipProps } from '@components/ProductOffers/misc/TVChannelLogos.styles';
import { ChannelLogo } from '@shared/components/display/ChannelLogo/ChannelLogo';
import { useChannelInfoTooltip } from '@hooks/useChannelInfoTooltip';

interface ThemePackageChannelLogoProps {
  channel: Channel;
}

/**
 * One channel logo with its info-card Tooltip. Lives in its own component so the
 * controlled-tooltip hook is called once per logo (a hook can't run inside the
 * parent's .map loop). On a fine pointer the hook is a passthrough, so the
 * Tooltip keeps its existing hover/focus behaviour.
 */
export const ThemePackageChannelLogo = ({ channel }: ThemePackageChannelLogoProps) => {
  const { tooltipProps, onTriggerTap } = useChannelInfoTooltip({ stopPropagation: true });

  // Stop propagation unconditionally so a logo tap never toggles the card on
  // either pointer type, then drive the coarse-pointer info-card via the hook.
  const handleLogoClick = (event: SyntheticEvent): void => {
    event.stopPropagation();
    onTriggerTap(event);
  };

  return (
    <Tooltip
      title={<ChannelHoverCard channel={channel} />}
      placement="top"
      componentsProps={channelTooltipProps}
      {...tooltipProps}
    >
      <Stack className="ThemePackageCard-logo" onClick={handleLogoClick}>
        <ChannelLogo src={channel.logo} name={channel.name} />
      </Stack>
    </Tooltip>
  );
};
